import { useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import type { OrderCountsBySize } from '../types/worker';

interface UseOrderCountsBySizeConfig {
  adminId: string | null;
}

interface UseOrderCountsBySizeReturn {
  counts: OrderCountsBySize;
  loading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
}

const DEFAULT_COUNTS: OrderCountsBySize = {
  all: 0,
  small: 0,
  medium: 0,
  large: 0,
};

export function useOrderCountsBySize({
  adminId,
}: UseOrderCountsBySizeConfig): UseOrderCountsBySizeReturn {
  const [counts, setCounts] = useState<OrderCountsBySize>(DEFAULT_COUNTS);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const refresh = useCallback(async () => {
    if (!adminId) {
      setCounts(DEFAULT_COUNTS);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data, error: rpcError } = await supabase.rpc(
        'get_worker_order_counts_by_size',
        { p_admin_id: adminId }
      );

      if (rpcError) {
        throw new Error(rpcError.message);
      }

      if (data && data.length > 0) {
        const row = data[0];
        setCounts({
          all: row.all_count || 0,
          small: row.small_count || 0,
          medium: row.medium_count || 0,
          large: row.large_count || 0,
        });
      } else {
        setCounts(DEFAULT_COUNTS);
      }
    } catch (err) {
      console.error('Failed to load order counts by size:', err);
      setError(err instanceof Error ? err : new Error('Unknown error'));
      setCounts(DEFAULT_COUNTS);
    } finally {
      setLoading(false);
    }
  }, [adminId]);

  return {
    counts,
    loading,
    error,
    refresh,
  };
}
